"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import type { ComponentProps } from "react";
import * as React from "react";

import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const Dialog = DialogPrimitive.Root;

type CSSPropertiesWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type DialogTriggerProps = React.ComponentProps<
  typeof DialogPrimitive.Trigger
> & {
  asChild?: boolean;
};

const DialogTrigger = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: DialogTriggerProps) => (
  <DialogPrimitive.Trigger
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </DialogPrimitive.Trigger>
);
DialogTrigger.displayName = "DialogTrigger";

const DialogPortal = (
  props: React.ComponentProps<typeof DialogPrimitive.Portal>,
) => <DialogPrimitive.Portal {...props} />;

type DialogCloseProps = React.ComponentProps<typeof DialogPrimitive.Close> & {
  asChild?: boolean;
};

const DialogClose = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: DialogCloseProps) => (
  <DialogPrimitive.Close
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </DialogPrimitive.Close>
);
DialogClose.displayName = "DialogClose";

export type DialogContentChrome = "default" | "ghost";

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) => (
  <DialogPrimitive.Backdrop
    forceRender
    className={(state) =>
      cn(
        `
          fixed inset-0 z-100 bg-black/80 grayscale backdrop-blur-xs
          transition-opacity duration-200
          data-ending-style:opacity-0 data-starting-style:opacity-0
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
DialogOverlay.displayName = "DialogOverlay";

export const dialogCloseClassName =
  "z-101 close-button absolute hover:bg-accent bg-background border text-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none data-[popup-open]:bg-accent data-[popup-open]:text-muted-foreground p-2 rounded-full";

type CompatFocusEvent = {
  defaultPrevented: boolean;
  preventDefault: () => void;
};

function createCompatFocusHandler(handler?: (event: CompatFocusEvent) => void) {
  if (!handler) {
    return undefined;
  }

  return () => {
    let defaultPrevented = false;
    handler({
      defaultPrevented,
      preventDefault: () => {
        defaultPrevented = true;
      },
    });
    return defaultPrevented ? false : true;
  };
}

type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Popup> & {
  bodyClassName?: string;
  chrome?: DialogContentChrome;
  closeClassName?: string;
  forceMount?: boolean;
  hideOverlay?: boolean;
  keepMounted?: boolean;
  onCloseAutoFocus?: (event: CompatFocusEvent) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onInteractOutside?: (event: PointerEvent) => void;
  onOpenAutoFocus?: (event: CompatFocusEvent) => void;
  portal?: boolean;
};

const DialogContent = ({
  bodyClassName,
  chrome = "default",
  className,
  closeClassName = "right-5 top-5",
  forceMount = false,
  hideOverlay = false,
  keepMounted = false,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onInteractOutside,
  onOpenAutoFocus,
  portal = true,
  children,
  style,
  ...props
}: DialogContentProps) => {
  void onEscapeKeyDown;
  void onInteractOutside;
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);
  const isGhost = chrome === "ghost";
  const keyboardAwareStyle: CSSPropertiesWithVars = {
    "--overlay-keyboard-inset-bottom":
      "var(--visual-keyboard-inset-bottom, 0px)",
    "--overlay-keyboard-inset-bottom-half":
      "var(--visual-keyboard-inset-bottom-half, 0px)",
    "--overlay-keyboard-offset-top": "var(--visual-keyboard-offset-top, 0px)",
    "--overlay-keyboard-offset-top-half":
      "var(--visual-keyboard-offset-top-half, 0px)",
    "--overlay-keyboard-available-height":
      "calc(100dvh - var(--overlay-keyboard-inset-bottom) - var(--overlay-keyboard-offset-top))",
    "--dialog-content-max-height":
      "calc(var(--overlay-keyboard-available-height) - 24px)",
    "--overlay-content-max-height": "var(--dialog-content-max-height)",
    "--dialog-content-center-y":
      "calc(50% + var(--overlay-keyboard-offset-top-half) - var(--overlay-keyboard-inset-bottom-half))",
    ...style,
  };

  return (
    <>
      {!portal && <div ref={setContainer} className="contents" />}
      {(portal || container) && (
        <DialogPortal
          keepMounted={keepMounted || forceMount}
          container={portal ? undefined : container}
        >
          {!hideOverlay && (
            <DialogOverlay
              className={
                isGhost ? "bg-black/10 grayscale-0 backdrop-blur-xs" : undefined
              }
            />
          )}
          <DialogPrimitive.Popup
            className={(state) =>
              cn(
                isGhost
                  ? `
                      fixed inset-x-0 top-0 bottom-(--overlay-keyboard-inset-bottom)
                      z-100 touch-pan-y overflow-y-auto overscroll-contain
                      outline-hidden
                    `
                  : `
                      fixed top-(--dialog-content-center-y) left-[50%]
                      z-100 max-h-(--dialog-content-max-height) w-full
                      max-w-[90vw] -translate-1/2
                      outline-0! transition duration-200 ease-out
                      data-ending-style:scale-95 data-ending-style:opacity-0
                      data-starting-style:scale-95 data-starting-style:opacity-0
                      md:w-full
                      md:max-w-lg
                    `,
                resolveClassName(className, state),
              )
            }
            finalFocus={createCompatFocusHandler(onCloseAutoFocus)}
            initialFocus={createCompatFocusHandler(onOpenAutoFocus)}
            style={keyboardAwareStyle}
            {...props}
          >
            <DialogClose
              onClick={(event) => event.stopPropagation()}
              className={cn(dialogCloseClassName, closeClassName)}
            >
              <Cross2Icon className="size-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
            <div
              className={cn(
                isGhost
                  ? `
                      grid min-h-full gap-0 overflow-visible border-0
                      bg-transparent shadow-none duration-200
                    `
                  : `
                      grid h-full gap-0 overflow-y-auto rounded-2xl border
                      bg-background shadow-lg
                    `,
                bodyClassName,
              )}
            >
              {children}
            </div>
          </DialogPrimitive.Popup>
        </DialogPortal>
      )}
    </>
  );
};
DialogContent.displayName = "DialogContent";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex p-6 pt-0", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={ref}
    className={(state) =>
      cn(
        `
          unforce-radius border-b p-6 font-sans text-lg/6 font-semibold
          text-muted-foreground
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
DialogTitle.displayName = "DialogTitle";

const DialogDescription = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={ref}
    className={(state) =>
      cn(
        "p-6 text-sm text-muted-foreground",
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
DialogDescription.displayName = "DialogDescription";

const DialogOverlayPrimitive = ({
  className,
  ...props
}: ComponentProps<"div">) => {
  return (
    <div
      className={cn(
        `
          fixed inset-0 z-100 bg-black/80 grayscale backdrop-blur-xs
          transition-opacity duration-200
        `,
        className,
      )}
      {...props}
    />
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogOverlayPrimitive,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
